<template>
  <CForm @submit.prevent="saveForm">
    <CCard>
      <CCardBody>
        <DotsLoader v-if="isLoading" />
        <div
          v-if="!allowPreview()"
          class="d-flex flex-row align-items-center"
          data-cy="form-submission-closed"
        >
          <h3>Ce questionnaire est clos.</h3>
          <img
            src="/img/form_blocked.svg"
            alt=""
            class="img-fluid ml-auto"
            style="max-width: 50%"
          />
        </div>
        <div
          data-cy="form-submitted-message"
          class="end-msg"
          v-else-if="isSent"
        >
          <h1 v-if="isSent" class="text-center">{{$t('thank_you_text')}}</h1>
          <img
            src="/img/form_completed.svg"
            alt=""
            class="img-fluid"
          />
        </div>
        <div v-else>
          <h1>{{$t(`survey_header${formIdSuffix}`)}}</h1>
          <p v-html="$t(firstParagraphKey)" v-if="$te(firstParagraphKey)"/>
          <div
            class="section"
            v-for="(sectionData, section) in visibleForm"
            :key="section"
            v-if="sectionData.fieldCount"
          >
            <h2>{{ section }}</h2>
            <FormField
              ref="formField"
              v-for="(field, index) in sectionData.fields"
              :field="getFieldData(field)"
              v-model="answers[field.name]"
              :key="`${field.name}_${index}_${answers[relationalFieldOptions[field.name]]}
                    _${publicInformationOptions}_${answers[field.name]}`"
              :highlightAnswer="didAnswerTriggeredQuestions(field)"
              :style="{ 'margin-left': calculateFieldIndent(field) }"
              @input="(content) => handleFormFieldValueChange(content, field)"
              :answer-copy="getAnswers"
            />
          </div>
        </div>
      </CCardBody>
      <CCardFooter
        class="d-flex justify-content-end"
        v-if="!isSent && !isLoading && campaignStatus !== 'clos'"
      >
        <CButton
          type="submit"
          color="primary"
          @click="handleSubmit()"
          :disabled="isSubmitDisabled"
        >
          Submit
        </CButton>
      </CCardFooter>
    </CCard>
  </CForm>
</template>

<script>
import { FormService } from "@/services/form";
import FormField from "@/components/FormField";
import { ActiveUser } from "@/services/user";
import { FonctionService } from '@/services/fonction';

const noneOptions = {
  fr: "Aucun de ces métiers",
  en: "None of these professions",
  zh: "以上都不是",
};

export default {
  name: "Form",
  components: { FormField },
  data() {
    return {
      relationalFieldOptions: {
        'commune_naissance': 'departement_naissance',
        'situation_formation_n-1': 'situation_formation_n-1_category',
        'ville_etudiant': 'code_postal_etudiant',
        'ville_parent': 'code_postal_parent',
      },
      isLoading: true,
      isSubmitDisabled: false,
      form: {},
      campaignStatus: null,
      campaignFormId: null,
      id: this.$route.params.formId,
      answers: {},
      isSent: false,
      language: "",
      certificationCode: null,
      userLanguage: null,
      fonctions: [],
      naOption: "",
      naPublicQuestions: {},
      recalculate: 0,
      sections: {
        en: ["Information about parents/legal guardians", "Educational background"],
        fr: ["Informations sur les parents / représentants légaux", "Mon parcours scolaire"],
        zh: []
      },
      codesFieldsOption: ['departement_bac'],
      codesFields: [
        'departement_bac',
        'code_postal_etudiant',
        'departement_naissance',
        'code_postal_parent',
        'situation_formation_n-1_category'
      ],
    };
  },
  watch: {
    publicInformationOptions(v) {
      if (!this.answers?.['signature_alumni'] || this.isLoading) {
        return
      }
      this.naPublicQuestions.forEach(field => {
        const question = Object.values(this.form).find(section => {
          return section.find(question => question.name === field);
        }).find(question => question.name === field);
        if (!v) {
          Object.keys(this.answers).forEach(answer => {
            if (this.answers[answer] === this.naOption.value) {
              delete this.answers[answer];
            }
          });
          question.options = question.options.filter((item) => item !== this.naOption);
        } else {
          question.options.push(this.naOption);
        }
      })
    }
  },
  mounted() {
    this.userLanguage = this.$i18n.locale;
    this.fetchForm();
  },
  beforeDestroy(){
    ActiveUser.setLanguage(this.userLanguage);
  },
  computed: {
    mapVisibleForm() {
      const filteredSections = Object.keys(this.visibleForm)
        .filter(key => this.sections[this.$route.query.lang.toLowerCase()].includes(key))
        .reduce((obj, key) => {
          obj[key] = this.visibleForm[key];
          return obj;
        }, {});
      return filteredSections
    },
    getAnswers() {
      let bac_category = ""
      let pcs_category_parent_1 = ""
      let pcs_category_parent_2 = ""
      const mapVisibleForm = this.mapVisibleForm
      const sections = this.sections[this.$route.query.lang.toLowerCase()]
      if (this.answers["pcs_category_parent_1"]) {
        const pcs_category_parent_1_field = mapVisibleForm[
          sections[0]
        ].fields.find(field => field.name === "pcs_category_parent_1");
        const pcs_parent_1_field = mapVisibleForm[
          sections[0]
        ].fields.find(field => field.name === "pcs_parent_1");
        if (pcs_category_parent_1_field) {
          pcs_category_parent_1 = pcs_category_parent_1_field.options.find(
              option => option.value === this.answers["pcs_category_parent_1"]
          ).answer_code;
          if (this.answers['pcs_parent_1']){
            this.deleteAnswerFromBacPcs(pcs_parent_1_field, pcs_category_parent_1)
          }
        }
      }
      if (this.answers["pcs_category_parent_2"]) {
        const pcs_category_parent_2_field = mapVisibleForm[
          sections[0]
        ].fields.find(field => field.name === "pcs_category_parent_2");
        const pcs_parent_2_field = mapVisibleForm[
          sections[0]
        ].fields.find(field => field.name === "pcs_parent_2");

        if (pcs_category_parent_2_field) {
          pcs_category_parent_2 = pcs_category_parent_2_field.options.find(
              option => option.value === this.answers["pcs_category_parent_2"]
          ).answer_code;
          if (this.answers['pcs_parent_2']){
            this.deleteAnswerFromBacPcs(pcs_parent_2_field, pcs_category_parent_2)
          }
        }
      }
      if (this.answers["bac_category"]) {
        const bac_category_field = mapVisibleForm[
          sections[1]
        ].fields.find(field => field.name === "bac_category");
        const type_bac_field = mapVisibleForm[
          sections[1]
        ].fields.find(field => field.name === "type_bac");
        if (bac_category_field) {
          bac_category = bac_category_field.options.find(
              option => option.value === this.answers["bac_category"]
          ).answer_code;
          if (this.answers['type_bac']){
            this.deleteAnswerFromBacPcs(type_bac_field, bac_category)
          }
        }
      }

      return {
        "pcs_category_parent_1": pcs_category_parent_1,
        "pcs_category_parent_2": pcs_category_parent_2,
        "bac_category": bac_category
      }
    },
    publicInformationOptions() {
      if (!this.answers || this.campaignFormId === 2 || this.isLoading) {
        return false
      }
      return this.answers['signature_alumni'] === "Des informations publiques issues des réseaux professionnels";
    },
    relationalFieldOptionsReversed(){
      const childParentRelation = {};
      return Object.keys(this.relationalFieldOptions).reduce((parentField, childKey) => {
        childParentRelation[this.relationalFieldOptions[childKey]] = childKey;
        return childParentRelation;
      }, {});
    },
    formIdSuffix(){
      if (this.language.toLowerCase() === 'fr' && this.campaignFormId !== 1) return `_${this.campaignFormId}`;
      return ''
    },
    firstParagraphKey() {
      return `survey_first_paragraph${this.formIdSuffix}`
    },
    fonctionOptions() {
      const options = this.fonctions.map((fonction) => ({
        value: fonction.fonction,
        label: fonction[`fonction_${this.language}`] || fonction.fonction,
      }));
      if (!options.find(({ value }) => value === noneOptions.fr)) {
        options.push({
          label: noneOptions[this.language],
          value: noneOptions.fr,
        });
      }
      return options;
    },
    visibleForm() {
      this.recalculate;
      const form = {};
      Object.entries(this.form).forEach(([section, fields]) => {
        const visibleFields = fields
          .filter(this.isFieldVisible)
          .map((field) => ({
            ...field,
            label: field.label.replaceAll("\n", "<br/>"),
            options: ["fonction", "fonction_2"].includes(field.name)
              ? this.fonctionOptions
              : field.options,
          }));
        form[section] = {
          fields: visibleFields,
          fieldCount: visibleFields.filter((field) => field.type !== "Text")
            .length,
        };
      });
      return form;
    }
  },
  methods: {
    handleSubmit(){
      this.$refs.formField.forEach((field) => field.handleFormSubmit());
    },
    handleFormFieldValueChange(content, field){
      const relatedChildField = this.relationalFieldOptionsReversed[field.name];
      if (field.name === 'departement_bac') {
        this.recalculate++;
      }
      if (relatedChildField) {
        this.answers[relatedChildField] = field.multiple ? [] : "";
        return this.getFieldData(field)
      }
    },
    getRelatedFieldOptions({name, options}){
      const relatedFieldData = this.answers?.[this.relationalFieldOptions?.[name]];
      return options?.[relatedFieldData] || [];
    },
    deleteAnswerFromBacPcs(field, category_code){
      const result = field.options[category_code].map((answer) => {
        return answer.value
      })
      if (!result.includes(this.answers[field.name])){
        delete this.answers[field.name]
      }
    },
    getFieldData(field){
      const resultField = { ...field };
      if (this.relationalFieldOptions[field.name]) {
        resultField.options = this.getRelatedFieldOptions(field);
      }
      return resultField;
    },
    calculateFieldIndent(field, acc=0) {
      if (field && field.conditions && acc < 5){
        const conditions = field.conditions.conditions || [field.conditions];
        const trueCondition = conditions.find(
          (condition) => condition.field && this.checkCondition(condition)
        );
        return this.calculateFieldIndent(
          Object.values(this.visibleForm)
            .flatMap((s) => s.fields)
            .find((f) => f.name === trueCondition.field),
          acc + 1
        );
      }
      return `${30 * acc}px`;
    },
    checkConditionTriggersField(condition, checkField) {
      if (!condition) {
        return false;
      }
      const { conditions, field } = condition;
      if (conditions) {
        return conditions.some((c) => this.checkConditionTriggersField(c, checkField));
      }
      return field === checkField;
    },
    didAnswerTriggeredQuestions(field){
      if (!this.answers[field.name]) return false;
      for (const section of Object.values(this.visibleForm)) {
        for (const sectionField of section.fields) {
          // we do not need to check if condition is met
          // because we are already looping over filtered form
          const didAnswerTrigger = this.checkConditionTriggersField(sectionField.conditions, field.name);
          if (didAnswerTrigger) return didAnswerTrigger;
        }
      }
      return false;
    },
    allowPreview() {
      const campaignIsClosed = this.campaignStatus === "clos";
      return !campaignIsClosed || ActiveUser.isAdministrator();
    },
    isFieldVisible({ conditions, name, options }) {
      const relatedName = this.relationalFieldOptions[name]
      if (relatedName) {
        if (!this.getRelatedFieldOptions({name, options}).includes(this.answers[name])){
          delete this.answers[name]
        }
        return this.getRelatedFieldOptions({name, options})?.length
      }
      if (["fonction", "fonction_2"].includes(name) && this.fonctionOptions.length < 2) {
        return false
      }
      if (!conditions) {
        return true;
      }
      const isVisible = this.checkCondition(conditions);
      if (!isVisible) {
        delete this.answers[name];
      }
      return isVisible;
    },
    parseAnswersIntoLabelValue(field, fieldList, lookLabel = true, returnData = false){
      if (fieldList.includes(field)){
        const sections = Object.values(this.form)
        const section = sections.find(section => section.find(f => f.name === field))
        if (section){
          const related_field_options = section.find(f => f.name === field).options
          let answer = null
          if (lookLabel){
            answer = related_field_options.find(option => option.value === this.answers[field])
          } else {
            answer = related_field_options.find(option => option.label === this.answers[field])
          }
          if (answer && !returnData){
            this.answers[field] = lookLabel ? answer.label : answer.value
          } else if (answer && returnData){
            return lookLabel ? answer.label : answer.value
          }
        }
      }
      return returnData ? this.answers[field] : null
    },
    checkCondition({ operator, field, value, conditions }) {
      if (operator === "and") {
        return conditions.every(this.checkCondition);
      } else if (operator === "or") {
        return conditions.some(this.checkCondition);
      }

      let fieldAnswer = this.parseAnswersIntoLabelValue(
          field,
          this.codesFieldsOption,
          true,
          true
      )
      if (!fieldAnswer) {
        return false;
      } else if (fieldAnswer === true) {
        fieldAnswer = "True";
      }

      if (operator === "eq") {
        return this.isEqualConditionMet(fieldAnswer, value)
      } if (operator === "not_eq") {
        return !this.isEqualConditionMet(fieldAnswer, value)
      } else if (operator === "in") {
        return value.includes(fieldAnswer);
      }
    },
    isEqualConditionMet(fieldAnswer, value){
      return Array.isArray(fieldAnswer) ? fieldAnswer.includes(value) : fieldAnswer === value;
    },
    async fetchFonctions() {
      const data = await FonctionService.fetchFonctions({
        filters: JSON.stringify({ search_certification_code: this.certificationCode, per_page: 1000 }),
      });
      this.fonctions = data.filter(data => data.certification_code === this.certificationCode);
      this.isLoading = false;
    },
    async fetchForm() {
      const { data, res } = await FormService.fetchForm(this.id);
      if (res.status !== 200) {
        return alert("Form not found");
      }
      if (data.is_loading) {
        return setTimeout(this.fetchForm, 500);
      }
      this.form = data.form;
      this.campaignStatus = data.campaign_status;
      this.campaignFormId = data.campaign_form_id;
      if (this.campaignFormId === 1) {
        this.naPublicQuestions = data.na_public.questions;
        this.naOption = data.na_public.na_option;
      }
      this.answers = { ...data.pre_fill, ...data.answers };
      const names = Object.keys(this.answers)
      names.forEach(name => {
        this.parseAnswersIntoLabelValue(name, this.codesFields ,false)
      })
      this.certificationCode = data.certification_code;
      this.language = this.campaignFormId === 2 ? 'fr' : data.language.toLowerCase();
      await ActiveUser.setLanguage(this.language);
      await this.fetchFonctions();
    },
    async saveForm() {
      this.isSubmitDisabled = true;
      const names = Object.keys(this.answers)
      names.forEach(name => {
        this.parseAnswersIntoLabelValue(name, this.codesFields)
      })
      await FormService.saveAnswers(this.id, this.answers);
      this.isSent = true;
    },
  },
};
</script>
<style lang="scss">
  .end-msg{
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
  }
  @media (max-width: map-get($container-max-widths, "sm")) {
  .end-msg{
    display: flex;
    flex-direction: column-reverse;
    align-items: center;
    .btn{
      margin-top: 1em;
    }
    h1 {
      margin-top: 0.5em;
    }
  }
}
</style>
